import { Component, ErrorInfo, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { AlertCircle, RefreshCw, Home } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  retryCount: number;
  correlationId: string | null;
}

function generateCorrelationId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = { hasError: false, error: null, retryCount: 0, correlationId: null };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error, correlationId: generateCorrelationId() };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const correlationId = this.state.correlationId || generateCorrelationId();
    console.error(`[${correlationId}] ErrorBoundary caught:`, error, errorInfo);

    // Log to backend (fire and forget)
    supabase.functions.invoke('log-client-error', {
      body: {
        message: error.message,
        stack: error.stack,
        correlationId,
        url: window.location.href,
        userAgent: navigator.userAgent,
      },
    }).catch(() => {});

    // Auto-retry on dynamic import failures (Vite HMR issue)
    if (
      error.message?.includes('Failed to fetch dynamically imported module') &&
      this.state.retryCount < 3
    ) {
      setTimeout(() => {
        this.setState((prev) => ({
          hasError: false,
          error: null,
          correlationId: null,
          retryCount: prev.retryCount + 1,
        }));
      }, 1000);
    }
  }

  public render() {
    if (this.state.hasError) {
      const isDynamicImport = this.state.error?.message?.includes('dynamically imported module');
      return (
        <div className="min-h-screen flex items-center justify-center bg-background px-4">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 rounded-2xl bg-destructive/10 flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-8 h-8 text-destructive" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">
              {isDynamicImport ? 'Page failed to load' : 'Something went wrong'}
            </h1>
            <p className="text-muted-foreground mb-2 text-sm">
              {isDynamicImport
                ? 'This usually happens after an update. A refresh should fix it.'
                : 'An unexpected error occurred. Please try refreshing.'}
            </p>
            {this.state.correlationId && (
              <p className="text-xs text-muted-foreground mb-6 font-mono">
                Reference: {this.state.correlationId}
              </p>
            )}
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => {
                  this.setState({ hasError: false, error: null, retryCount: 0, correlationId: null });
                  window.location.reload();
                }}
                className="inline-flex items-center justify-center rounded-lg bg-primary text-primary-foreground px-5 py-2.5 text-sm font-medium hover:bg-primary/90 transition-colors gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
              <button
                onClick={() => { window.location.href = '/'; }}
                className="inline-flex items-center justify-center rounded-lg border border-border text-foreground px-5 py-2.5 text-sm font-medium hover:bg-muted transition-colors gap-2"
              >
                <Home className="w-4 h-4" />
                Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
